import * as THREE from 'three'
import type { CharacterPresentationFrame, CharacterPresenter } from './CharacterPresenter'

/** Options for the fallback capsule character presenter. */
export interface CapsuleCharacterPresenterOptions {
  /** Character id used for naming scene objects. */
  characterId: string
  /** Scene root receiving the visible capsule. */
  sceneRoot: THREE.Object3D
  /** Scene root receiving debug helpers. */
  debugRoot: THREE.Object3D
  /** Capsule display color. */
  color?: THREE.ColorRepresentation
  /** Capsule opacity. */
  opacity?: number
}

const CAPSULE_AXIS = new THREE.Vector3(0, 1, 0)
const DEFAULT_COLOR = '#ff9559'
const DEFAULT_OPACITY = 1.0

/** Default capsule presenter for the character runtime. */
export class CapsuleCharacterPresenter implements CharacterPresenter {
  /** Visible capsule object. */
  readonly object: THREE.Group
  /** Debug helper object for direction visualization. */
  readonly debugObject: THREE.Group

  private readonly mesh: THREE.Mesh
  private readonly material: THREE.MeshBasicMaterial
  private readonly bodyArrow: THREE.ArrowHelper
  private readonly aimArrow: THREE.ArrowHelper
  private currentRadius = -1
  private currentLength = -1

  constructor(options: CapsuleCharacterPresenterOptions) {
    this.object = new THREE.Group()
    this.object.name = `characterPresenter:${options.characterId}`

    this.material = new THREE.MeshBasicMaterial({
      color: options.color ?? DEFAULT_COLOR,
      depthTest: true,
      depthWrite: true,
      transparent: true,
      opacity: options.opacity ?? DEFAULT_OPACITY,
    })

    this.mesh = new THREE.Mesh(new THREE.CapsuleGeometry(0.5, 1), this.material)
    this.mesh.name = `characterCapsule:${options.characterId}`
    this.object.add(this.mesh)
    options.sceneRoot.add(this.object)

    this.debugObject = new THREE.Group()
    this.debugObject.name = `characterDebug:${options.characterId}`
    this.debugObject.visible = false
    this.bodyArrow = new THREE.ArrowHelper(
      new THREE.Vector3(1, 0, 0),
      new THREE.Vector3(),
      1,
      0x42d392
    )
    this.aimArrow = new THREE.ArrowHelper(
      new THREE.Vector3(1, 0, 0),
      new THREE.Vector3(),
      1,
      0xffd166
    )
    this.debugObject.add(this.bodyArrow, this.aimArrow)
    options.debugRoot.add(this.debugObject)
  }

  /**
   * Updates the capsule transform and debug helpers.
   *
   * @param frame - Character presentation frame.
   */
  update(frame: CharacterPresentationFrame): void {
    const capsule = frame.snapshot.capsule
    const segment = capsule.end.clone().sub(capsule.start)
    const length = Math.max(1e-4, segment.length())
    if (
      Math.abs(capsule.radius - this.currentRadius) > 1e-6 ||
      Math.abs(length - this.currentLength) > 1e-6
    ) {
      this.mesh.geometry.dispose()
      this.mesh.geometry = new THREE.CapsuleGeometry(capsule.radius, length)
      this.currentRadius = capsule.radius
      this.currentLength = length
    }

    const center = capsule.start.clone().add(capsule.end).multiplyScalar(0.5)
    const direction =
      segment.lengthSq() > Number.EPSILON ? segment.normalize() : new THREE.Vector3(0, 0, 1)
    this.object.position.copy(center)
    this.object.quaternion.setFromUnitVectors(CAPSULE_AXIS, direction)

    this.debugObject.position.copy(frame.anchors.root.position)
    this.debugObject.quaternion.copy(frame.anchors.root.quaternion)
    this.bodyArrow.setDirection(frame.bodyDirection)
    this.bodyArrow.setLength(Math.max(0.75, frame.planarSpeed + 0.5))
    this.aimArrow.position.copy(frame.anchors.eye.position.clone().sub(frame.anchors.root.position))
    this.aimArrow.setDirection(frame.aimDirection)
    this.aimArrow.setLength(1.25)
  }

  /**
   * Sets visible capsule visibility.
   *
   * @param visible - Whether the presenter should be visible.
   */
  setVisible(visible: boolean): void {
    this.object.visible = visible
  }

  /** Releases geometries, materials, and scene objects. */
  dispose(): void {
    this.mesh.geometry.dispose()
    this.material.dispose()
    this.object.removeFromParent()
    this.debugObject.removeFromParent()
    this.object.clear()
    this.debugObject.clear()
  }
}
